import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { lazy, Suspense, Component, ReactNode, useEffect } from "react";
import { ApolloProvider } from "@apollo/client/react";
import { apolloClient } from "@/lib/graphql/apollo";
import { UserProvider } from "@/contexts/UserContext";
import { useIsReadOnly } from "@/lib/rbac";
import { TasksProvider } from "@/contexts/TasksContext";
import { PipelinesProvider } from "@/contexts/PipelinesContext";
import { PreviewAddedDealsProvider } from "@/contexts/PreviewAddedDealsContext";
import { TerminologyProvider } from "@/contexts/TerminologyContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { QuotasProvider } from "@/contexts/QuotasContext";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { DealFieldsProvider } from "@/contexts/DealFieldsContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ImportProgressProvider } from "@/contexts/ImportProgressContext";
import { AuthProvider } from "@/components/providers/AuthProvider";
import AppLayout from "@/components/layout/AppLayout";
import { PostHogProvider } from "@/components/providers/PostHogProvider";
import { LanguageSync } from "@/components/providers/LanguageSync";
import { isLovablePreview } from "@/lib/utils/environment";
import { WorkspaceRemount } from "@/components/layout/WorkspaceRemount";

/**
 * Wraps React.lazy() with automatic retry + reload for stale chunk errors.
 * After a Vercel deployment, old chunk hashes no longer exist on the CDN.
 * If a dynamic import fails (e.g., 404 on a hashed .js file), this helper
 * retries once, then forces a full page reload to pick up the new manifest.
 * A sessionStorage flag prevents infinite reload loops.
 */
function lazyWithRetry<T extends { default: React.ComponentType<unknown> }>(
  importFn: () => Promise<T>,
): React.LazyExoticComponent<T["default"]> {
  return lazy(() =>
    importFn()
      .then((module) => {
        // Successful import — clear the reload flag so future deployments can trigger reload
        sessionStorage.removeItem("chunk-reload");
        return module;
      })
      .catch((error: unknown) => {
        // Retry once -- the import cache may have been cleared
        return importFn().catch(() => {
          // If we already reloaded for this error, don't loop
          const reloadKey = "chunk-reload";
          const hasReloaded = sessionStorage.getItem(reloadKey);
          if (!hasReloaded) {
            sessionStorage.setItem(reloadKey, "1");
            window.location.reload();
          }
          // Re-throw so the error boundary can catch it if reload didn't help
          throw error;
        });
      }),
  );
}

/**
 * Error boundary that catches chunk load failures (stale deployments).
 * Shows a brief message and reloads the page to fetch fresh assets.
 */
class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('[ErrorBoundary] Render error:', error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const isChunk =
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("Loading chunk") ||
      error.name === "ChunkLoadError";
    if (isChunk) {
      const reloadKey = "chunk-reload";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
      }
    } else {
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen w-screen bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }
    return this.props.children;
  }
}

const TodayPage = lazyWithRetry(() => import("./pages/TodayPage"));
const PeoplePage = lazyWithRetry(() => import("./pages/PeoplePage"));
const CompaniesPage = lazyWithRetry(() => import("./pages/CompaniesPage"));
const DealsPage = lazyWithRetry(() => import("./pages/DealsPage"));
const NotesPage = lazyWithRetry(() => import("./pages/NotesPage"));
const TasksPage = lazyWithRetry(() => import("./pages/TasksPage"));

const AgentsPage = lazyWithRetry(() => import("./pages/AgentsPage"));
const FavoritesPage = lazyWithRetry(() => import("./pages/FavoritesPage"));
const ReportsPage = lazyWithRetry(() => import("./pages/ReportsPage"));
const ReportViewPage = lazyWithRetry(() => import("./pages/ReportViewPage"));
const ListBuilderPage = lazyWithRetry(() => import("./pages/ListBuilderPage"));
const CampaignsPage = lazyWithRetry(() => import("./pages/CampaignsPage"));
const SyncProgressPreviewPage = lazyWithRetry(() => import("./pages/SyncProgressPreviewPage"));
const DesktopDownloadPreviewPage = lazyWithRetry(() => import("./pages/DesktopDownloadPreviewPage"));
const WorkEmailRequiredPage = lazyWithRetry(() => import("./pages/WorkEmailRequiredPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const HelpPage = lazyWithRetry(() => import("./pages/HelpPage"));
const SetupPage = lazyWithRetry(() => import("./pages/SetupPage"));
const InviteTeamPage = lazyWithRetry(() => import("./pages/InviteTeamPage"));
const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const ExtensionLoginPage = lazyWithRetry(() => import("./pages/ExtensionLoginPage"));
const CallbackPage = lazyWithRetry(() => import("./pages/CallbackPage"));
const CallbackLegacyPage = lazyWithRetry(() => import("./pages/CallbackLegacyPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const SharedMeetingPage = lazyWithRetry(() => import("./pages/SharedMeetingPage"));
const SharedClipPage = lazyWithRetry(() => import("./pages/SharedClipPage"));
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage"));
const ChooseWorkspacePage = lazyWithRetry(() => import("./pages/ChooseWorkspacePage"));
const SubscriptionExpiredPage = lazyWithRetry(() => import("./pages/SubscriptionExpiredPage"));
const PlansPage = lazyWithRetry(() => import("./pages/PlansPage"));
const StripeCallbackPage = lazyWithRetry(() => import("./pages/StripeCallbackPage"));
const McpCallbackPage = lazyWithRetry(() => import("./pages/McpCallbackPage"));
const MobileApp = lazyWithRetry(() => import("./pages/mobile/MobileApp"));
const DesktopApp = lazyWithRetry(() => import("./pages/desktop/DesktopApp"));
const OAuthSimPage = lazyWithRetry(() => import("./pages/OAuthSimPage"));
const EditorPage = lazyWithRetry(() => import("./pages/EditorPage"));

// Legacy fallback: /onboard → /onboarding (backend now redirects directly to /onboarding)
function OnboardRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/onboarding${search}`} replace />;
}

// /ask is deprecated — Ask Coffee is now exclusively the slide-in panel.
// We auto-open the panel (with the optional ?q= prefilled) and redirect home.
function AskRedirect() {
  const { search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search).get("q");
    if (q) window.dispatchEvent(new CustomEvent("openAskCoffeeWithQuery", { detail: q }));
    else window.dispatchEvent(new CustomEvent("openAskCoffee"));
  }, [search]);
  return <Navigate to="/" replace />;
}

// Legacy redirects: preserve query params (e.g. ?workspace_id=...) from coffee-ui URLs
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// Entity detail redirect: strips trailing sub-paths (summary, meetings, etc.) and preserves query params
function DashboardDetailRedirect({ base }: { base: string }) {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${base}/${id}${search}`} replace />;
}

// Route guard that bounces read-only users to the home page. Lead finder
// and campaigns are mutation-heavy features (creating lists, sending
// outbound) and not useful for read-only callers.
function ReadOnlyRedirect({ children }: { children: ReactNode }) {
  const isReadOnly = useIsReadOnly();
  if (isReadOnly) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const queryClient = new QueryClient();

function LoginRoute() {
  return isLovablePreview() ? <Navigate to="/" replace /> : <LoginPage />;
}

function ExtensionLoginRoute() {
  return isLovablePreview() ? <Navigate to="/" replace /> : <ExtensionLoginPage />;
}

/** Toaster that renders inside the mobile app frame when on /mobile. */
const AppToaster = () => {
  const { pathname } = useLocation();
  const isMobile = pathname.startsWith("/mobile");

  if (!isMobile) return <Sonner />;

  return (
    <Sonner
      position="bottom-center"
      offset={96}
      style={{ ["--width" as string]: "min(398px, calc(100vw - 32px))" } as React.CSSProperties}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-xl group-[.toaster]:text-sm",
          description: "group-[.toast]:text-muted-foreground group-[.toast]:text-xs",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
    />
  );
};

const App = () => (
  <ApolloProvider client={apolloClient}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <UserProvider>
          <PostHogProvider>
          <CreditsProvider>
          <ImportProgressProvider>
          <FavoritesProvider>
          <QuotasProvider>
          <TasksProvider>
          <PipelinesProvider>
          <PreviewAddedDealsProvider>
          <DealFieldsProvider>
          <CurrencyProvider>
          <TerminologyProvider>
          <TooltipProvider>
          <LanguageSync />
          <AppToaster />
          <ChunkErrorBoundary>
          <Suspense fallback={
            <div className="flex items-center justify-center h-screen w-screen bg-background">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          }>
            <Routes>
              <Route element={<LoginRoute />} path="/login" />
              <Route element={<ExtensionLoginRoute />} path="/login/extension" />
              <Route element={<CallbackPage />} path="/callback" />
              <Route element={<CallbackLegacyPage />} path="/callback/legacy" />
              <Route element={<OnboardingPage />} path="/onboarding" />
              <Route element={<OnboardRedirect />} path="/onboard" />
              <Route element={<ChooseWorkspacePage />} path="/choose-workspace" />
              <Route element={<SharedMeetingPage />} path="/shared/meeting/:id" />
              <Route element={<SharedClipPage />} path="/shared/clip/:slug" />
              <Route element={<SetupPage />} path="/setup" />
              <Route element={<SubscriptionExpiredPage />} path="/subscription-expired" />
              <Route element={<PlansPage />} path="/plans" />
              <Route element={<StripeCallbackPage />} path="/stripe/callback" />
              {/* One redirect URL for every MCP provider — the connection is found by `state`. */}
              <Route element={<McpCallbackPage />} path="/integrations/mcp/callback" />
              <Route element={<OAuthSimPage />} path="/oauth-sim" />
              <Route element={<EditorPage />} path="/editor-debug" />

              <Route element={<AppLayout><TodayPage /></AppLayout>} path="/" />
              <Route element={<AskRedirect />} path="/ask" />
              {/* key per mode: both routes render NotesPage at the same tree position,
                  so without a key React reconciles instead of remounting when navigating
                  between /meetings and /notes — stale page state (hasLoadedOnce, selection,
                  search, localStorage-keyed filter/sort hooks) then leaks across modes and
                  the loading skeleton never shows. */}
              <Route element={<AppLayout><NotesPage key="meetings" mode="meetings" /></AppLayout>} path="/meetings" />
              <Route element={<AppLayout><NotesPage key="notes" mode="notes" /></AppLayout>} path="/notes" />
              <Route element={<AppLayout><TasksPage /></AppLayout>} path="/tasks" />

              <Route element={<AppLayout><WorkspaceRemount><PeoplePage /></WorkspaceRemount></AppLayout>} path="/people" />
              <Route element={<AppLayout><WorkspaceRemount><PeoplePage /></WorkspaceRemount></AppLayout>} path="/people/:id" />
              <Route element={<AppLayout><WorkspaceRemount><CompaniesPage /></WorkspaceRemount></AppLayout>} path="/companies" />
              <Route element={<AppLayout><WorkspaceRemount><CompaniesPage /></WorkspaceRemount></AppLayout>} path="/companies/:id" />
              <Route element={<AppLayout><WorkspaceRemount><DealsPage /></WorkspaceRemount></AppLayout>} path="/deals" />
              <Route element={<AppLayout><WorkspaceRemount><DealsPage /></WorkspaceRemount></AppLayout>} path="/deals/:id" />

              {/* Legacy coffee-ui URL redirects */}
              {/* Entity detail pages (catch trailing sub-paths like /summary, /meetings, etc.) */}
              <Route element={<DashboardDetailRedirect base="people" />} path="/dashboard/people/:id/*" />
              <Route element={<DashboardDetailRedirect base="companies" />} path="/dashboard/companies/:id/*" />
              <Route element={<DashboardDetailRedirect base="deals" />} path="/dashboard/deals/:id/*" />
              {/* List pages */}
              <Route element={<LegacyRedirect to="/people" />} path="/dashboard/people" />
              <Route element={<LegacyRedirect to="/companies" />} path="/dashboard/companies" />
              <Route element={<LegacyRedirect to="/deals" />} path="/dashboard/deals" />
              <Route element={<LegacyRedirect to="/notes" />} path="/dashboard/notes" />
              <Route element={<LegacyRedirect to="/notes" />} path="/dashboard/meeting-notes" />
              {/* Dashboard root / today */}
              <Route element={<LegacyRedirect to="/" />} path="/dashboard" />
              <Route element={<LegacyRedirect to="/" />} path="/dashboard/today" />
              <Route element={<LegacyRedirect to="/tasks" />} path="/dashboard/today/tasks" />
              {/* Settings & admin */}
              <Route element={<LegacyRedirect to="/settings" />} path="/dashboard/settings/*" />
              <Route element={<LegacyRedirect to="/settings" />} path="/dashboard/admin/*" />
              <Route element={<AppLayout><FavoritesPage /></AppLayout>} path="/favorites" />
              <Route element={<AppLayout><ReportsPage /></AppLayout>} path="/reports" />
              <Route element={<AppLayout><ReportViewPage /></AppLayout>} path="/reports/:template" />
              <Route element={<AppLayout><AgentsPage /></AppLayout>} path="/agents" />
              <Route element={<ReadOnlyRedirect><AppLayout><ListBuilderPage /></AppLayout></ReadOnlyRedirect>} path="/list-builder" />
              <Route element={<ReadOnlyRedirect><AppLayout><CampaignsPage /></AppLayout></ReadOnlyRedirect>} path="/campaigns/*" />
              <Route element={<ReadOnlyRedirect><AppLayout><CampaignsPage /></AppLayout></ReadOnlyRedirect>} path="/campaigns" />
              <Route element={<SyncProgressPreviewPage />} path="/preview/sync-progress" />
              <Route element={<DesktopDownloadPreviewPage />} path="/preview/desktop-download" />
              <Route element={<DesktopDownloadPreviewPage />} path="/preview/desktop-download/:platform" />
              <Route element={<WorkEmailRequiredPage />} path="/preview/work-email-required" />
              {/* Real destination when the backend refuses a free-email signup
                  (403 free_email_signup_blocked, routed from the Apollo error link). */}
              <Route element={<WorkEmailRequiredPage />} path="/work-email-required" />
              <Route element={<AppLayout><SettingsPage /></AppLayout>} path="/settings" />
              <Route element={<AppLayout><HelpPage /></AppLayout>} path="/help" />
              <Route element={<AppLayout><InviteTeamPage /></AppLayout>} path="/invite" />

              <Route element={<MobileApp />} path="/mobile" />
              <Route element={<DesktopApp />} path="/desktop" />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </TooltipProvider>
        </TerminologyProvider>
        </CurrencyProvider>
        </DealFieldsProvider>
        </PreviewAddedDealsProvider>
        </PipelinesProvider>
        </TasksProvider>
        </QuotasProvider>
        </FavoritesProvider>
        </ImportProgressProvider>
        </CreditsProvider>
          </PostHogProvider>
          </UserProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ApolloProvider>
);

export default App;
